// [UPDATED] Requirement 3: KPI has no separate score or weight (KPIs feed their KRA).
export const DEFAULT_WEIGHTS = { goals: 50, kra: 50, kpi: 0 }; // same as Scoring & ratings (Goals 50%, KRA 50%)

export const DEFAULT_BANDS = [
  { min: 90, max: 100, label: "Exceptional", tone: "success" },
  { min: 80, max: 89.99, label: "Exceeds Expectations", tone: "primary" },
  { min: 70, max: 79.99, label: "Meets Expectations", tone: "info" },
  { min: 60, max: 69.99, label: "Needs Improvement", tone: "warning" },
  { min: 0, max: 59.99, label: "Unsatisfactory", tone: "danger" },
];

export const NOT_RATED = "Not Yet Rated";

const clamp = (value, min = 0, max = 100) => Math.min(max, Math.max(min, value));

function toNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

export function round2(value) {
  return value === null || value === undefined ? null : Math.round(value * 100) / 100;
}

export function goalAchievement(goal) {
  const manager = toNumber(goal.managerAchievement);
  const own = toNumber(goal.achievement);
  const value = manager ?? own;
  return value === null ? null : clamp(value);
}

export function calcGoalScore(goals = []) {
  const weighted = goals.filter((g) => Number(g.weightage) > 0);
  if (!weighted.length || weighted.every((g) => goalAchievement(g) === null)) return null;
  const totalWeight = weighted.reduce((sum, g) => sum + Number(g.weightage), 0);
  const total = weighted.reduce((sum, g) => sum + (goalAchievement(g) ?? 0) * Number(g.weightage), 0);
  return total / totalWeight;
}

export function itemAchievement(item, direction = "higher") {
  const target = toNumber(item.target);
  const actual = toNumber(item.actual);
  if (target === null || actual === null) return null;
  if (direction === "lower") {
    if (actual <= 0) return 100;
    return clamp((target / actual) * 100);
  }
  if (direction === "target") {
    if (target === 0) return actual === 0 ? 100 : 0;
    return clamp(100 - (Math.abs(actual - target) / Math.abs(target)) * 100);
  }
  if (target === 0) return 100;
  return clamp((actual / target) * 100);
}

export function calcWeightedItems(items = [], getDirection = () => "higher") {
  const weighted = items.filter((i) => Number(i.weightage) > 0);
  if (!weighted.length) return null;
  const scored = weighted.map((i) => ({ w: Number(i.weightage), s: itemAchievement(i, getDirection(i)) }));
  if (scored.every((x) => x.s === null)) return null;
  const totalWeight = scored.reduce((sum, x) => sum + x.w, 0);
  return scored.reduce((sum, x) => sum + (x.s ?? 0) * x.w, 0) / totalWeight;
}

export function calcFinalScore({ goalScore, kraScore, kpiScore }, weights = DEFAULT_WEIGHTS) {
  const parts = [
    [goalScore, weights.goals],
    [kraScore, weights.kra],
    [kpiScore, weights.kpi],
  ];
  if (parts.every(([score]) => score === null || score === undefined)) return null;
  return parts.reduce((sum, [score, weight]) => sum + (score ?? 0) * (Number(weight) / 100), 0);
}

export function getRating(score, bands = DEFAULT_BANDS) {
  if (score === null || score === undefined || Number.isNaN(Number(score))) {
    return { label: NOT_RATED, tone: "neutral" };
  }
  const s = round2(Number(score));
  const sorted = [...bands].sort((a, b) => b.min - a.min);
  const band = sorted.find((b) => s >= Number(b.min) && s <= Number(b.max)) || (s > 100 ? sorted[0] : null);
  return band ? { label: band.label, tone: band.tone || "neutral" } : { label: NOT_RATED, tone: "neutral" };
}

export function computePerformance({ goals = [], kras = [], kpis = [], kpiMaster = [], config }) {
  const weights = config?.weights || DEFAULT_WEIGHTS;
  const bands = config?.bands || DEFAULT_BANDS;
  const directionOf = (kpi) => kpi.direction || kpiMaster.find((m) => m.id === kpi.kpiId)?.direction || "higher";
  const goalScore = calcGoalScore(goals);
  const hierarchical = kpis.some((kpi) => kpi.parentKraItemId);
  let kraScore;
  let kpiScore;
  let effectiveWeights = { ...weights };
  let kraBreakdown = [];

  if (hierarchical) {
    kraBreakdown = kras.map((kra) => {
      const children = kpis.filter((kpi) => kpi.parentKraItemId === kra.id);
      // [UPDATED] KPI weights are equal shares (or 0 in older data): the KRA is then the average of its KPIs.
      const noKpiWeight = children.every((kpi) => !(Number(kpi.weightage) > 0));
      const score = children.length && children.every((kpi) => itemAchievement(kpi, directionOf(kpi)) !== null)
        ? calcWeightedItems(noKpiWeight ? children.map((kpi) => ({ ...kpi, weightage: 1 })) : children, directionOf)
        : null;
      return {
        kraItemId: kra.id,
        name: kra.name,
        weight: Number(kra.weightage),
        score: round2(score),
        kpis: children.map((kpi) => ({
          performanceItemId: kpi.id,
          name: kpi.name,
          weight: Number(kpi.weightage),
          score: round2(itemAchievement(kpi, directionOf(kpi))),
        })),
      };
    });
    const complete = kraBreakdown.length > 0 && kraBreakdown.every((row) => row.score !== null);
    kraScore = complete
      ? calcWeightedItems(kraBreakdown.map((row) => ({ weightage: row.weight, target: 100, actual: row.score })))
      : null;
    kpiScore = null;
    effectiveWeights = { goals: Number(weights.goals), kra: Number(weights.kra) + Number(weights.kpi), kpi: 0 };
  } else {
    kraScore = calcWeightedItems(kras);
    kpiScore = calcWeightedItems(kpis, directionOf);
  }
  const finalScore = calcFinalScore({ goalScore, kraScore, kpiScore }, effectiveWeights);
  const rating = getRating(finalScore, bands);
  return {
    goalScore: round2(goalScore),
    kraScore: round2(kraScore),
    kpiScore: round2(kpiScore),
    finalScore: round2(finalScore),
    rating: rating.label,
    ratingTone: rating.tone,
    scoringMode: hierarchical ? "HIERARCHICAL_KRA_KPI" : "FLAT",
    effectiveWeights,
    kraBreakdown,
  };
}

export function toneForRating(label, bands = DEFAULT_BANDS) {
  return bands.find((b) => b.label === label)?.tone || "neutral";
}
